import { cellsForColumn } from './plottrackGrid';
import { OUTLINE_CHAPTER_OUTSIDE } from './writerCopy';

const PLACEHOLDER_PART = /^(sequence|act|chapter)\s*\d+$/i;

export function partLabel(sequence) {
  const name = String(sequence?.display_name || '').trim();
  if (!name || PLACEHOLDER_PART.test(name)) return '';
  return name;
}

export function partExample(sequence) {
  if (!sequence) return '';
  const meta = sequence.metadata || {};
  const raw = sequence.example
    || sequence.summary
    || sequence.description
    || sequence.note
    || meta.example
    || meta.summary
    || '';
  return String(raw || '').trim();
}

function scenesFor(sequence, byId, used) {
  const members = [];
  (sequence?.scene_ids || []).forEach((id) => {
    const column = byId.get(String(id));
    if (!column || used.has(String(column.scene_id))) return;
    used.add(String(column.scene_id));
    members.push(column);
  });
  return members;
}

/** A sequence the upload created, kept beside a Framework rather than seated on it. */
export function sequenceFromUpload(sequence) {
  if (!sequence || typeof sequence !== 'object') return false;
  if (sequence.from_upload === true) return true;
  const meta = sequence.metadata;
  return Boolean(meta && typeof meta === 'object' && meta.source === 'prose_upload');
}

function partFromSequence(sequence, members) {
  return {
    sequence,
    scenes: members,
    planned: members.length > 0,
    loose: false,
  };
}

/**
 * One horizontal card row per part. Sequences win over acts.
 * A placeholder part with no scenes and no example is not shown.
 * Framework names, when present, are the part list: each name is a part,
 * and an outline sequence lines up with it by name, then by order.
 * Sequences that came from an upload stay in front of that list, in
 * their own order, with their scenes. Other leftovers still sit on the
 * first Framework part, so a 16-sequence Framework does not number a
 * scene 17.
 */
export function outlineParts({ columns, headers, frameworkNames = [] }) {
  const scenes = (columns || []).filter((column) => column?.scene_id);
  const byId = new Map(scenes.map((column) => [String(column.scene_id), column]));
  const sequences = headers?.sequences || [];
  const acts = headers?.acts || [];
  const groups = sequences.length ? sequences : acts;
  const used = new Set();
  const parts = [];
  const names = (frameworkNames || [])
    .map((name) => String(name || '').trim())
    .filter((name) => name && !PLACEHOLDER_PART.test(name));

  if (names.length) {
    const uploadGroups = [];
    const pool = [];
    groups.forEach((sequence) => {
      if (sequenceFromUpload(sequence)) uploadGroups.push(sequence);
      else pool.push(sequence);
    });
    uploadGroups.forEach((sequence) => {
      const members = scenesFor(sequence, byId, used);
      if (!members.length && !partLabel(sequence) && !partExample(sequence)) return;
      parts.push(partFromSequence(sequence, members));
    });
    const frameworkParts = [];
    names.forEach((name) => {
      const exact = pool.findIndex((sequence) => partLabel(sequence).toLowerCase() === name.toLowerCase());
      const placeholder = pool.findIndex((sequence) => !partLabel(sequence));
      const index = exact >= 0 ? exact : placeholder;
      const sequence = index >= 0 ? pool.splice(index, 1)[0] : null;
      const members = scenesFor(sequence, byId, used);
      frameworkParts.push({
        sequence: sequence
          ? { ...sequence, display_name: partLabel(sequence) || name }
          : { display_name: name },
        scenes: members,
        planned: members.length > 0,
        loose: false,
      });
    });
    const unmatched = [];
    pool.forEach((sequence) => {
      unmatched.push(...scenesFor(sequence, byId, used));
    });
    if (unmatched.length && frameworkParts[0]) {
      frameworkParts[0].scenes = [...frameworkParts[0].scenes, ...unmatched];
      frameworkParts[0].planned = true;
    }
    parts.push(...frameworkParts);
  } else {
    groups.forEach((sequence) => {
      const members = scenesFor(sequence, byId, used);
      const planned = members.length > 0;
      if (!planned && !partLabel(sequence) && !partExample(sequence)) return;
      parts.push(partFromSequence(sequence, members));
    });
  }

  const loose = scenes.filter((column) => !used.has(String(column.scene_id)));
  const firstFramework = names.length
    ? parts.find((part) => !sequenceFromUpload(part.sequence) && !part.loose)
    : null;
  if (loose.length && firstFramework) {
    firstFramework.scenes = [...firstFramework.scenes, ...loose];
    firstFramework.planned = true;
  } else if (loose.length) {
    parts.push({
      sequence: null,
      scenes: loose,
      planned: true,
      loose: true,
    });
  }

  return parts.map((part, index) => ({ ...part, number: index + 1 }));
}

const EMPTY_LANE_PREFIX = 'empty-lane:';

/**
 * Plot Grid columns plus a lane for each empty sequence.
 * With no scenes, the grid already draws unplanned sequence columns.
 * Once any scene exists, those empty sequences used to disappear.
 * Card order is the order here: upload parts, then the Framework.
 */
export function outlineGridColumns({ columns, headers, frameworkNames = [] }) {
  const source = columns || [];
  if (!source.some((column) => column?.scene_id)) {
    return { columns: source, headers, inserted: false };
  }
  const parts = outlineParts({ columns: source, headers, frameworkNames });
  const hasEmptyLane = parts.some((part) => !part.scenes.length && partLabel(part.sequence));
  if (!hasEmptyLane) return { columns: source, headers, inserted: false };

  const nextColumns = [];
  const sequences = [];
  parts.forEach((part) => {
    const name = partLabel(part.sequence);
    if (part.scenes.length) {
      part.scenes.forEach((column) => nextColumns.push(column));
      if (part.sequence) {
        sequences.push({
          ...part.sequence,
          scene_ids: part.scenes.map((column) => column.scene_id),
        });
      }
      return;
    }
    if (!name) return;
    const laneId = `${EMPTY_LANE_PREFIX}${part.sequence?.id || name}`;
    nextColumns.push({
      id: laneId,
      scene_id: laneId,
      display_name: name,
      emptyLane: true,
    });
    sequences.push({
      ...(part.sequence || {}),
      id: part.sequence?.id || laneId,
      display_name: part.sequence?.display_name || name,
      scene_ids: [laneId],
      emptyLane: true,
    });
  });
  return {
    columns: nextColumns,
    headers: {
      ...(headers || {}),
      acts: headers?.acts || [],
      chapters: headers?.chapters || [],
      sequences,
    },
    inserted: true,
  };
}

/** A chapter still sitting on the upload, beside a Framework's sequences. */
export function outsideSequenceHint(part, frameworkApplied, chapterName) {
  const name = String(chapterName || '').trim();
  if (!frameworkApplied || !name) return '';
  if (!part || part.loose || !sequenceFromUpload(part.sequence)) return '';
  return OUTLINE_CHAPTER_OUTSIDE(name);
}

export function sceneCardBlurb(column, cells, rows) {
  const related = cellsForColumn(column, cells);
  const mainId = (rows || []).find((row) => row.type === 'main')?.id;
  const ordered = [...related].sort((a, b) => {
    const aMain = mainId && String(a.row_id) === String(mainId) ? 0 : 1;
    const bMain = mainId && String(b.row_id) === String(mainId) ? 0 : 1;
    return aMain - bMain;
  });
  for (const cell of ordered) {
    if (cell?.state === 'absent') continue;
    for (const beat of cell?.beats || []) {
      const text = typeof beat === 'string' ? beat : (beat?.action || beat?.text || '');
      if (String(text).trim()) return String(text).trim();
    }
    if (String(cell?.note || '').trim()) return String(cell.note).trim();
  }
  return '';
}

/** Scene ids in the order the cards are drawn, part by part. */
export function readingOrderSceneIds(parts) {
  const ids = [];
  (parts || []).forEach((part) => {
    (part.scenes || []).forEach((column) => {
      if (column?.scene_id) ids.push(String(column.scene_id));
    });
  });
  return ids;
}

/**
 * The card just before this one. "Start a chapter here" closes the
 * chapter on that scene. Grid column order can put a later card first;
 * the cards the writer sees are the order that counts.
 */
export function previousSceneInReadingOrder(parts, sceneId) {
  const ids = readingOrderSceneIds(parts);
  const index = ids.indexOf(String(sceneId));
  if (index <= 0) return null;
  return ids[index - 1];
}

export function chapterCoveringScene(chapters, sceneId) {
  const id = String(sceneId || '');
  if (!id) return null;
  return (chapters || []).find((chapter) => (
    (chapter?.scene_ids || []).some((scene) => String(scene) === id)
  )) || null;
}

/**
 * A saved break closes on its last scene. The chapter the writer started
 * begins on the next scene and runs through the next break.
 * Scenes before the first break are left out: #1820 does not say whether
 * they are a chapter, so this does not name them.
 */
export function chaptersStartingAfterBreaks(chapters, readingOrderIds) {
  const ids = (readingOrderIds || []).map((id) => String(id)).filter(Boolean);
  const indexOf = new Map(ids.map((id, index) => [id, index]));
  const closed = (chapters || []).map((chapter) => {
    const sceneIds = (chapter?.scene_ids || [])
      .map((id) => String(id))
      .filter((id) => indexOf.has(id));
    if (!sceneIds.length) return null;
    return {
      chapter,
      closeAt: indexOf.get(sceneIds[sceneIds.length - 1]),
      closedAt: sceneIds[sceneIds.length - 1],
    };
  }).filter(Boolean).sort((a, b) => a.closeAt - b.closeAt);

  return closed.map((item, index) => {
    const start = item.closeAt + 1;
    const end = index + 1 < closed.length ? closed[index + 1].closeAt : ids.length - 1;
    if (start > end || start >= ids.length) return null;
    return {
      ...item.chapter,
      scene_ids: ids.slice(start, end + 1),
      closed_at_scene_id: item.closedAt,
    };
  }).filter(Boolean);
}

/** The chapter whose first scene is this card. Empty chapters draw nothing. */
export function chapterOpeningAt(chapters, sceneId) {
  const id = String(sceneId || '');
  if (!id) return null;
  return (chapters || []).find((chapter) => (
    String((chapter?.scene_ids || [])[0] || '') === id
  )) || null;
}

/** Drop the dragged scene in front of the target. Dropping on itself is a no-op. */
export function dropBeforeScene(part, draggedId, targetId) {
  if (!part || !draggedId || String(draggedId) === String(targetId)) return null;
  const ids = (part.scenes || []).map((scene) => String(scene.scene_id));
  const without = ids.filter((id) => id !== String(draggedId));
  const index = targetId ? without.indexOf(String(targetId)) : without.length;
  const at = index < 0 ? without.length : index;
  const sequenceId = part.sequence?.id || undefined;
  if (at === 0) return { sequenceId, atStart: true };
  return { sequenceId, afterSceneId: without[at - 1] };
}
